/**
 * Logger utility for conditional logging
 *
 * Usage:
 *   import { logger } from '@/utils/logger'
 *
 *   logger.debug('Debug message', { data })  // Only in development
 *   logger.info('Info message')              // Always logged
 *   logger.warn('Warning message')           // Always logged
 *   logger.error('Error message', error)     // Always logged
 */

const isDev = import.meta.env.DEV

export const logger = {
  /**
   * Debug logging - only in development mode
   */
  debug: (...args: any[]) => {
    if (isDev) {
      console.log('[DEBUG]', ...args)
    }
  },

  /**
   * Info logging - logged in all environments
   */
  info: (...args: any[]) => {
    console.log('[INFO]', ...args)
  },

  /**
   * Warning logging - logged in all environments
   */
  warn: (...args: any[]) => {
    console.warn('[WARN]', ...args)
  },

  /**
   * Error logging - logged in all environments
   */
  error: (...args: any[]) => {
    console.error('[ERROR]', ...args)
  },

  /**
   * Group logging - only in development mode
   */
  group: (label: string, fn: () => void) => {
    if (isDev) {
      console.group(label)
      fn()
      console.groupEnd()
    }
  },

  /**
   * Table logging - only in development mode
   */
  table: (data: any) => {
    if (isDev) {
      console.table(data)
    }
  },

  /**
   * Time measurement - only in development mode
   */
  time: (label: string) => {
    if (isDev) {
      console.time(label)
    }
  },

  timeEnd: (label: string) => {
    if (isDev) {
      console.timeEnd(label)
    }
  }
}

// Export type for TypeScript
export type Logger = typeof logger
